import { FC, useEffect, useRef, useState } from 'react'
import { AdCategoryDropdown } from './AdCategoryDropdown'
import { LocationDropdown } from './LocationDropdown'
import { SearchDropdown } from './SearchDropdown'
import classes from './adLibraryWidget.module.scss'

export const AdLibraryWidget: FC = () => {
  const [locationOpen, setLocationOpen] = useState(false)
  const [categoryOpen, setCategoryOpen] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<string>('Georgia')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const locationRef = useRef<HTMLDivElement>(null)
  const categoryRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        locationRef.current &&
        !locationRef.current.contains(e.target as Node)
      ) {
        setLocationOpen(false)
      }
      if (
        categoryRef.current &&
        !categoryRef.current.contains(e.target as Node)
      ) {
        setCategoryOpen(false)
      }
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setSearchOpen(e.target.value.length > 0)
  }

  const handleSearchClear = () => {
    setSearchQuery('')
    setSearchOpen(false)
  }

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <h1 className={classes.cardTitle}>Ad Library</h1>
        <p className={classes.cardSubtitle}>
          Discover trending ads from competitors and platforms
        </p>
      </div>

      <div className={classes.filtersRow}>
        <div className={classes.filterGroup} ref={locationRef}>
          <button
            className={
              locationOpen
                ? `${classes.filterGroupLeft} ${classes.filterGroupLeftActive}`
                : classes.filterGroupLeft
            }
            onClick={() => {
              setLocationOpen((v) => !v)
              setCategoryOpen(false)
            }}
            type="button"
          >
            {selectedLocation}
            <svg
              fill="none"
              height="16"
              viewBox="0 0 16 16"
              width="16"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 6L8 10L12 6"
                stroke="#25262D"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
            </svg>
          </button>

          <div className={classes.filterGroupRightWrapper} ref={categoryRef}>
            <button
              className={
                categoryOpen
                  ? `${classes.filterGroupRight} ${classes.filterGroupRightActive}`
                  : classes.filterGroupRight
              }
              onClick={() => {
                setCategoryOpen((v) => !v)
                setLocationOpen(false)
              }}
              type="button"
            >
              {selectedCategory || 'Ad category'}
              <svg
                fill="none"
                height="16"
                viewBox="0 0 16 16"
                width="16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="#25262D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                />
              </svg>
            </button>

            {categoryOpen && (
              <AdCategoryDropdown
                onClose={() => setCategoryOpen(false)}
                onSelect={setSelectedCategory}
                selected={selectedCategory}
              />
            )}
          </div>

          {locationOpen && (
            <LocationDropdown
              onClose={() => setLocationOpen(false)}
              onSelect={setSelectedLocation}
              selected={selectedLocation}
            />
          )}
        </div>

        <div
          className={`${classes.searchWrapper} ${searchQuery ? classes.searchWrapperActive : ''}`}
          ref={searchRef}
        >
          <input
            className={classes.searchInput}
            onChange={handleSearchChange}
            placeholder="Search by keyword or advertiser"
            type="text"
            value={searchQuery}
          />
          {searchQuery && (
            <button
              className={classes.searchClear}
              onClick={handleSearchClear}
              type="button"
            >
              <svg
                fill="none"
                height="16"
                viewBox="0 0 16 16"
                width="16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 4L4 12M4 4L12 12"
                  stroke="#86949D"
                  strokeLinecap="round"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
          )}
          {searchOpen && searchQuery.length > 0 && (
            <SearchDropdown
              adCategory={selectedCategory}
              onClose={() => setSearchOpen(false)}
              query={searchQuery}
            />
          )}
        </div>
      </div>
    </div>
  )
}
